// types for coffee posting status system - similar to art_of_the_day
export type CoffeeSubmissionStatus =
    | 'idle'           // initial state, not submitted yet
    | 'validating'     // validating form data
    | 'creating'       // creating coffee entry in database
    | 'uploading'      // uploading image to server
    | 'processing'     // server processing the submission
    | 'completed'      // successfully completed
    | 'failed'         // submission failed
    | 'error'          // unexpected error occurred
    | 'retrying';      // retrying after failure

// detailed status information
export interface CoffeeSubmissionStatusInfo {
    status: CoffeeSubmissionStatus;
    message?: string;
    progress?: number;  // percentage for progress bar (0-100)
    error?: string;
    errorDetails?: string;  // additional error context
    failedStep?: string;    // id of the step that failed
    failedFormStep?: number; // form step number (1-4) where failure occurred
    startedAt?: Date;
    completedAt?: Date;
    retryCount?: number;
    maxRetries?: number;
}

// submission result interface
export interface CoffeeSubmissionResult {
    success: boolean;
    coffeeId?: string;
    message?: string;
    error?: string;
    statusInfo: CoffeeSubmissionStatusInfo;
}

// progress step tracking
export interface CoffeeSubmissionStep {
    id: string;
    name: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    message?: string;
    timestamp?: Date;
}

// comprehensive submission state
export interface CoffeeSubmissionState {
    isSubmitting: boolean;
    currentStatus: CoffeeSubmissionStatus;
    statusInfo: CoffeeSubmissionStatusInfo;
    steps: CoffeeSubmissionStep[];
    result?: CoffeeSubmissionResult;
}

// default submission steps
export const DEFAULT_COFFEE_SUBMISSION_STEPS: CoffeeSubmissionStep[] = [
    { id: 'validation', name: 'Validating form data', status: 'pending' },
    { id: 'creation', name: 'Creating coffee entry', status: 'pending' },
    { id: 'image_upload', name: 'Uploading image', status: 'pending' },
    { id: 'processing', name: 'Processing submission', status: 'pending' },
    { id: 'completion', name: 'Finalizing entry', status: 'pending' }
];

// helper function to get status color
export const getCoffeeStatusColor = (status: CoffeeSubmissionStatus): string => {
    switch (status) {
        case 'completed':
            return '#2ecc71'; // green for completed
        case 'creating':
        case 'uploading':
        case 'processing':
            return '#f6b93b'; // orange for active processing
        case 'validating':
        case 'retrying':
            return '#925ed1'; // purple for validation/retry
        case 'idle':
            return '#52b7ff'; // light blue for idle
        case 'failed':
        case 'error':
            return '#e74c3c'; // red for errors
        default:
            return '#95a5a6'; // gray for unknown states
    }
};

// helper function to get status message
export const getCoffeeStatusMessage = (status: CoffeeSubmissionStatus): string => {
    switch (status) {
        case 'idle':
            return 'ready';
        case 'validating':
            return 'validating';
        case 'creating':
            return 'creating';
        case 'uploading':
            return 'uploading';
        case 'processing':
            return 'processing';
        case 'completed':
            return 'completed';
        case 'failed':
            return 'failed';
        case 'error':
            return 'error';
        case 'retrying':
            return 'retrying';
        default:
            // this should never happen, but TypeScript requires it
            return String(status).toLowerCase();
    }
}; 